import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #variantUpdateToken = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const updateToken = ++this.#variantUpdateToken;
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (this.#hasSameMediaOrder(newMediaGallery)) return;

    const currentHeight = this.getBoundingClientRect().height;
    const mediaColumn = this.closest('.product-information__media');
    const currentColumnHeight = mediaColumn?.getBoundingClientRect().height || currentHeight;

    if (currentHeight > 0) {
      newMediaGallery.style.minHeight = `${currentHeight}px`;
    }

    if (mediaColumn instanceof HTMLElement && currentColumnHeight > 0) {
      mediaColumn.style.minHeight = `${currentColumnHeight}px`;
    }

    await this.#waitForPrimaryImage(newMediaGallery);

    if (!this.isConnected || updateToken !== this.#variantUpdateToken) return;

    this.replaceWith(newMediaGallery);

    this.#releaseReservedHeight(newMediaGallery, mediaColumn);
  };

  /**
   * Releases temporary height reservations after the new gallery has laid out.
   * @param {Element} newMediaGallery
   * @param {Element | null} mediaColumn
   */
  #releaseReservedHeight(newMediaGallery, mediaColumn) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(() => {
          if (newMediaGallery instanceof HTMLElement) {
            newMediaGallery.style.minHeight = '';
          }

          if (mediaColumn instanceof HTMLElement) {
            mediaColumn.style.minHeight = '';
          }
        }, 250);
      });
    });
  }

  /**
   * Checks whether a variant update would render the same media in the same order.
   * Size-only updates should not replace the gallery.
   * @param {Element} newMediaGallery
   */
  #hasSameMediaOrder(newMediaGallery) {
    const currentIds = this.#mediaIds(this);
    const newIds = this.#mediaIds(newMediaGallery);

    return currentIds.length > 0 && currentIds.join('|') === newIds.join('|');
  }

  /**
   * Gets media ids from a media gallery.
   * @param {Element} gallery
   */
  #mediaIds(gallery) {
    return Array.from(gallery.querySelectorAll('.product-media[data-media-id]'))
      .map((media) => media.getAttribute('data-media-id'))
      .filter(Boolean);
  }

  /**
   * Keeps the current gallery visible until the incoming primary image is ready.
   * @param {Element} newMediaGallery
   */
  async #waitForPrimaryImage(newMediaGallery) {
    const image =
      newMediaGallery.querySelector('slideshow-slide:not([hidden]) img.product-media__image') ||
      newMediaGallery.querySelector('img.product-media__image');

    if (!(image instanceof HTMLImageElement)) return;
    if (image.complete && image.naturalWidth > 0) return;

    if (typeof image.decode === 'function') {
      try {
        await image.decode();
      } catch {
        // Keep the current gallery stable and let the browser render the replacement anyway.
      }
      return;
    }

    await new Promise((resolve) => {
      const timeout = window.setTimeout(resolve, 1200);
      const done = () => {
        window.clearTimeout(timeout);
        resolve(undefined);
      };

      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
